export const YES_NO_DROPDOWN = {
  type: "dropdown",
  type2: false,
  value1: "Yes,No",
  value2: "",
  checked: false,
  remote: false,
  prohibitInput: true,
  hintShow: false,
  hintText: "",
};

export const PRE_SCREENING_DROPDOWN = {
  ...YES_NO_DROPDOWN,
  value1: "Yes,No,No Need",
};

export const STATUS_DROPDOWN = {
  ...YES_NO_DROPDOWN,
  value1: "Active,Inactive",
};

export function normalizeSheetCellText(cell) {
  if (cell == null) return "";
  const raw = typeof cell === "object" ? `${cell.m ?? cell.v ?? ""}` : `${cell}`;
  return `${raw}`.replace(/\s+/g, " ").trim();
}

export function cloneAdmissionRecords(customer) {
  const records = customer?.admission_records;
  if (!Array.isArray(records) || records.length === 0) return [];
  try {
    return JSON.parse(JSON.stringify(records));
  } catch (_err) {
    return [];
  }
}

export function formatAdmissionDateDisplay(raw) {
  const text = `${raw ?? ""}`.trim();
  if (!text) return "";
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const mdy = text.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (mdy) {
    const mm = String(mdy[1]).padStart(2, "0");
    const dd = String(mdy[2]).padStart(2, "0");
    let year = mdy[3];
    if (year.length === 2) year = `20${year}`;
    return `${mm}/${dd}/${year}`;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return `${String(parsed.getMonth() + 1).padStart(2, "0")}/${String(parsed.getDate()).padStart(
    2,
    "0"
  )}/${parsed.getFullYear()}`;
}

export function admissionDateSavePayload(admissionRecords, admissionDate) {
  const date = formatAdmissionDateDisplay(admissionDate);
  const records = Array.isArray(admissionRecords)
    ? admissionRecords.map((record, index) =>
        index === 0 ? { ...record, admission_date: date } : { ...record }
      )
    : [];
  if (records.length > 0) {
    return { admission_date: date, admission_records: records };
  }
  return { admission_date: date };
}

function preferredNameFromCustomer(customer) {
  const text = `${customer?.name_cn ?? ""}`.trim();
  const open = text.lastIndexOf("(");
  const close = text.lastIndexOf(")");
  if (open >= 0 && close > open) {
    const inside = text.slice(open + 1, close).trim();
    if (inside) return inside;
  }
  return text;
}

/** Last Name, First Name Middle Name (Preferred Name). */
export function formatMedicalFormCustomerName(customer) {
  if (customer == null) return "";
  const last = `${customer.lastname ?? customer.lastName ?? ""}`.trim();
  const first = `${customer.firstname ?? customer.firstName ?? ""}`.trim();
  const middle = `${customer.middle_name ?? customer.middleName ?? ""}`.trim();
  const preferred = preferredNameFromCustomer(customer);
  const firstMiddle = [first, middle].filter(Boolean).join(" ");
  let main = "";
  if (last) {
    main = firstMiddle ? `${last}, ${firstMiddle}` : last;
  } else if (firstMiddle) {
    main = firstMiddle;
  } else {
    main = `${customer.name ?? ""}`.trim();
  }
  if (!main) return preferred;
  if (preferred && preferred.toLowerCase() !== main.toLowerCase()) {
    return `${main} (${preferred})`;
  }
  return main;
}

function nameField(obj, keys) {
  for (const key of keys) {
    const value = `${obj?.[key] ?? ""}`.trim();
    if (value) return value;
  }
  return "";
}

/** Sort by last name, then first, then middle. */
export function compareMedicalFormCustomersByName(a, b) {
  const cmp = (left, right) =>
    left.localeCompare(right, ["en", "zh-Hans"], { sensitivity: "base", numeric: true });
  const byLast = cmp(
    nameField(a, ["lastname", "lastName", "last_name"]),
    nameField(b, ["lastname", "lastName", "last_name"])
  );
  if (byLast !== 0) return byLast;
  const byFirst = cmp(
    nameField(a, ["firstname", "firstName", "first_name"]),
    nameField(b, ["firstname", "firstName", "first_name"])
  );
  if (byFirst !== 0) return byFirst;
  const byMiddle = cmp(
    nameField(a, ["middle_name", "middleName"]),
    nameField(b, ["middle_name", "middleName"])
  );
  if (byMiddle !== 0) return byMiddle;
  return cmp(nameField(a, ["name"]), nameField(b, ["name"]));
}

/** Parse "Last, First Middle (Preferred Name)" or "First Last (Preferred Name)". */
export function parseFullNamePreferred(value, fallback = {}) {
  const displayName = `${value ?? ""}`.trim();
  const match = displayName.match(/^(.*?)(?:\s*\(([^()]*)\))?$/);
  const mainName = `${match?.[1] ?? displayName}`.trim();
  const nameCn = `${match?.[2] ?? fallback.nameCn ?? ""}`.trim();
  if (mainName.includes(",")) {
    const comma = mainName.indexOf(",");
    const lastname = mainName.slice(0, comma).trim();
    const given = mainName.slice(comma + 1).trim();
    return {
      firstname: given || `${fallback.firstName ?? ""}`.trim(),
      lastname: lastname || `${fallback.lastName ?? ""}`.trim(),
      name_cn: nameCn,
    };
  }
  const parts = mainName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return {
      firstname: `${fallback.firstName ?? ""}`.trim(),
      lastname: `${fallback.lastName ?? ""}`.trim(),
      name_cn: nameCn,
    };
  }
  if (parts.length === 1) {
    return {
      firstname: parts[0],
      lastname: `${fallback.lastName ?? ""}`.trim(),
      name_cn: nameCn,
    };
  }
  return {
    firstname: parts.slice(0, -1).join(" "),
    lastname: parts[parts.length - 1],
    name_cn: nameCn,
  };
}

export function namePayloadIfChanged(row, nextName) {
  const prev = `${row?.name ?? ""}`.replace(/\s+/g, " ").trim();
  const next = `${nextName ?? ""}`.replace(/\s+/g, " ").trim();
  if (prev === next) return null;
  return parseFullNamePreferred(next, row);
}

export function yesNoPayload(value) {
  const normalised = `${value ?? ""}`.trim().toLowerCase().replace(/[_-]+/g, " ");
  if (normalised === "yes") return "yes";
  if (normalised === "no") return "no";
  if (normalised === "no need") return "no_need";
  return "";
}

export function displayYesNoValue(value) {
  const normalised = `${value ?? ""}`.trim().toLowerCase().replace(/[_-]+/g, " ");
  if (normalised === "yes" || normalised === "true") return "Yes";
  if (normalised === "no" || normalised === "false") return "No";
  if (normalised === "no need") return "No Need";
  return "";
}

export function statusPayload(value) {
  return `${value ?? ""}`.trim().toLowerCase();
}

export function displayStatusValue(value) {
  const raw = `${value ?? ""}`.trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

export function customerNameFields(customer) {
  return {
    firstName: `${customer?.firstname ?? customer?.firstName ?? ""}`.trim(),
    lastName: `${customer?.lastname ?? customer?.lastName ?? ""}`.trim(),
    middleName: `${customer?.middle_name ?? customer?.middleName ?? ""}`.trim(),
    nameCn: `${customer?.name_cn ?? ""}`.trim(),
  };
}

const MEDICAL_FORM_CUSTOMER_FIELD_TAB = {
  burial_arrangements: "medicalInsurance",
  molst: "medicalInsurance",
  provisions_for_advance_medical: "medicalInsurance",
  power_of_attorney: "medicalInsurance",
  hospice: "medicalInsurance",
  diabetes_mellitus: "medicalInsurance",
  weight: "medicalInsurance",
  eyes_on: "medicalInsurance",
  disability: "medicalInsurance",
  has_airtag: "medicalInsurance",
  rounding_notes: "medicalInsurance",
  tb_screening_yes_no: "medicalInsurance",
  pre_screening_yes_no: "medicalInsurance",
  forty_five_day_admission_note: "medicalInsurance",
  tb_notes: "medicalInsurance",
  ltss_id_number: "medicalInsurance",
  adcaps_completed_date: "medicalInsurance",
  adcaps_note: "medicalInsurance",
  medication_type: "medicalInsurance",
  medication_note: "medicalInsurance",
  pillbox_type: "medicalInsurance",
  pillbox_note: "medicalInsurance",
  status: "personalInfo",
  admission_date: "personalInfo",
  admission_records: "personalInfo",
  medicaid_number: "confidentialDetails",
};

export function filterMedicalFormCustomerUpdates(updates = [], canEditTab) {
  if (typeof canEditTab !== "function") return [];
  return (updates || [])
    .map((update) => {
      const { customerId, ...fields } = update || {};
      const allowed = {};
      Object.entries(fields).forEach(([key, value]) => {
        const tab = MEDICAL_FORM_CUSTOMER_FIELD_TAB[key];
        if (tab && canEditTab(tab)) allowed[key] = value;
      });
      return Object.keys(allowed).length ? { customerId, ...allowed } : null;
    })
    .filter(Boolean);
}
